import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Step {
  id: string;
  label: string;
  description?: string;
}

interface WizardStepperProps {
  steps: Step[];
  currentStep: number;
}

export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentStep,
}) => {
  const totalSteps = steps.length;
  const progressPercent = totalSteps > 0 ? (currentStep / (totalSteps - 1)) * 100 : 0;

  return (
    <nav
      aria-label="Progress"
      className="pr-4 lg:pr-8"
    >
      <div className="mb-6">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Progress
        </p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
      </div>
      <ol className="flex flex-row lg:flex-col gap-4 lg:gap-0 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
        {steps.map((step, index) => {
          const isComplete = index < currentStep;
          const isCurrent = index === currentStep;
          const isUpcoming = index > currentStep;
          const isLast = index === steps.length - 1;

          return (
            <li key={step.id} className="relative flex flex-col items-center lg:items-start shrink-0 lg:shrink pb-0 lg:pb-6 last:pb-0">
              {!isLast && (
                <div
                  className={cn(
                    "absolute transition-colors left-5 top-10 w-0.5 -ml-px hidden lg:block",
                    isComplete ? "bg-primary" : "bg-muted"
                  )}
                  style={{ height: "calc(100% - 0.25rem)" }}
                />
              )}
              <div className="flex flex-col items-center lg:items-start">
                <div
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all z-10",
                    isComplete &&
                      "border-primary bg-primary text-primary-foreground",
                    isCurrent &&
                      "border-primary bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.2)]",
                    isUpcoming &&
                      "border-muted-foreground/30 bg-muted/50 text-muted-foreground"
                  )}
                >
                  {isComplete ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span
                      className={cn(
                        "text-sm font-medium",
                        isCurrent && "text-primary"
                      )}
                    >
                      {index + 1}
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  <span
                    className={cn(
                      "text-sm font-medium block",
                      isComplete && "text-primary",
                      isCurrent && "text-foreground font-semibold",
                      isUpcoming && "text-muted-foreground"
                    )}
                  >
                    {step.label}
                  </span>
                  {step.description && (
                    <span
                      className={cn(
                        "text-xs block mt-0.5",
                        isCurrent ? "text-muted-foreground" : "text-muted-foreground/80"
                      )}
                    >
                      {step.description}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
